(ns client-portal.client.voice-insights.sessions.list.views
  (:require [client-portal.client.layout :as l]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.components.components :as c]
            [shared.core.reactive :as re]
            [client-portal.client.voice-insights.sessions.data :as sessions.d]
            [shared.core.amelia-light.components.components :as alc]
            [client-portal.client.voice-insights.tag-data :as tag.d]
            [client-portal.client.voice-insights.tags.data :as tags.d]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [client-portal.client.vi.components.tag :as tag]
            [client-portal.client.vi.components.tag-icon :as tag-icon]
            [shared.core.time :as t]
            [client-portal.client.voice-insights.data :as vi.d]
            [shared.core.account.data :as account.d]
            [clojure.string :as s]))

(defn pipe-separated-header-list [{:keys [container-style labels label-style]}]
  (into [:div {:style (merge {:display :flex
                              :align-items :center
                              :margin-left 8}
                             container-style)}]
        (map-indexed (fn [idx l]
                       [:span {:style (cond-> (merge {:line-height "16px"
                                                      :padding "0px 6px"}
                                                     label-style)
                                              (not= idx 0)
                                              (assoc :border-left "1px solid rgba(0, 0, 0, 0.06)"))}
                        l])
                     labels)))

(defn sentiment-face [{:keys [size sentiment]}]
  [:svg {:width size :height size :view-box (condp = sentiment
                                              "positive" "0 0 16 16"
                                              "negative" "32 0 16 16"
                                              "16 0 16 16")}
   [:use {:xlink-href "/images/sentiment.svg"}]])

(defn tag-by-name [{:keys [tags name]}]
  (some->> (when (not-empty tags) tags)
           (filterv :name)
           (filter #(s/includes? (util/->kebab-case-string (:name %))
                                 (util/->kebab-case-string name)))
           (first)))

(defn tag-material-icon [{:keys [name size] :or {size 16} :as args}]
  (let [tag (tag-by-name args)
        tag-value (:value tag)]
    (if tag-value
      (cond
        (#{"size-upsell" "combo-upsell" "add-on-upsell" "caller-left-voicemail" "caller-identified-automatically"
           "first-contact-resolution"} name)
        [c/material-icon
         (cond
           (contains? #{"offered" "true"} tag-value)
           {:icon-type :check
            :style {:color (style/color :green :shade-8)
                    :font-size size}}

           :else
           {:icon-type :close
            :style {:color (style/color :red :shade-6)
                    :font-size size}})]

        (#{"greeting" "sentiment"} name)
        [sentiment-face {:size size
                         :sentiment tag-value}])
      [:span ""])))

(defn outcome-icon [{:keys [tag]}]
  [alc/tooltip {:tooltip-content (tag/tag->display-name (dissoc tag :name))
                :target-content [:div {:style {:display :flex
                                               :align-items :center
                                               :justify-content :center
                                               :padding 2
                                               :height 16}}
                                 [tag-icon/tag-icon {:tag tag
                                                     :color (tag-icon/outcome-color tag)}]]
                :style {:height 16}}])

(defn outcome-label [{:keys [tag]}]
  (let [display-name (tag/tag->display-name (dissoc tag :name))]
    [:div {:title display-name
           :style {:color (style/alc-color "Text/primary")
                   :font-size 12
                   :padding-left 4
                   :text-overflow :ellipsis
                   :overflow :hidden
                   :white-space :nowrap}}
     display-name]))

(defn score-tag [{:keys [score score-thresholds low-score-best? container-style style]
                  :or {score-thresholds [25 75]}}]
  (let [color (when score
                (if low-score-best?
                  (cond
                    (> score (last score-thresholds)) (style/alc-color "Background/error_const")
                    (> score (first score-thresholds)) (style/alc-color "Background/alert_const")
                    :else (style/alc-color "Background/success_const"))
                  (cond
                    (< score (first score-thresholds)) (style/alc-color "Background/error_const")
                    (< score (last score-thresholds)) (style/alc-color "Background/alert_const")
                    :else (style/alc-color "Background/success_const"))))]
    [:div {:style (merge {:flex "0 0 45px"}
                         container-style)}
     [:div {:style (cond-> {:display :flex
                            :align-items :center
                            :justify-content :center
                            :border-radius 6
                            :padding "2px 0"
                            :width 34}
                           color (assoc :background-color color))}
      [:span {:style (merge {:font-size 12
                             :line-height 1.33
                             :color (if score
                                      (style/alc-color "Text/primary_const")
                                      (style/alc-color "Text/primary"))}
                            style)}
       (or score "-")]]]))


(defn sessions-list []
  (let [sessions- (re/subscribe [:sessions/list-flattened-])
        location-id->label- (re/subscribe [:locations/location-id->label-by-current-concept-id-])
        loading?- (re/subscribe [:sessions/list-loading?-])
        has-more?- (re/subscribe [:sessions/list-data-has-more?-])
        next-page-loading?- (re/subscribe [:sessions/list-next-page-loading?-])
        next-page- (re/subscribe [:sessions/list-next-page-])
        filter-container-height- (re/subscribe [:sessions/filter-container-height-])
        crew-type- (re/subscribe [:core/selected-crew-type-])
        admin?- (re/subscribe [:account/admin-role?-])
        show-menu-item-tags? @(re/subscribe [:sessions/show-menu-item-tags?-])
        ces-scores? @(re/subscribe [:voice-insights/ces-feature-enabled?-])
        user-roles (util/when-not-loading @(re/subscribe [:account/user-roles-]))
        role-hidden-tag-names (->> tag.d/sessions-table-hidden-utterance-tag-names-without-roles
                                   (filterv (fn [[_ required-roles]]
                                              (or (empty? user-roles)
                                                  (not (account.d/has-roles-access? user-roles required-roles)))))
                                   (mapv first)
                                   (into #{}))
        ai? (= @crew-type- "ai")
        show-convo-details? @admin?-
        show-locations-column? (and (not (util/loading? @location-id->label-))
                                    (> (count @location-id->label-) 1))
        selected-conversation-id (some-> @(re/subscribe [:sessions/selected-conversation-])
                                         :conversation-id)
        satisfaction-name- (re/subscribe [:voice-insights/satisfaction-name-])]
    [alc/table {:no-data-text "No sessions to display"
                :loading-more? @next-page-loading?-
                :loading? @loading?-
                :scroll-container-id l/core-scroll-content-id
                :bottom-px-buffer 200
                :on-bottom-buffer (when @has-more?-
                                    #(re/dispatch [:sessions/load-list-data [nil {:page @next-page-}]]))
                :has-more-rows? true
                :expanded-form-path sessions.d/table-columns-path
                :flex-vals (filterv identity
                                    [(when show-convo-details? "0 0 100px")
                                     "0 0 120px"
                                     (when show-locations-column? "0 0 180px")
                                     "0 0 64px"
                                     (if ces-scores?
                                       "0 0 64px"
                                       "0 0 74px")
                                     "0 0 74px"
                                     "0 0 70px"             ;"0 0 78px"
                                     "0 0 78px"
                                     "1 0 200px"])
                :sticky-headers? true
                :sticky-header-x-scroll? true
                :no-data-material-icon :inventory_2
                :no-data-style empty-state/base-container-style
                :no-data-text-style {:color (style/alc-color "Text/primary_header")
                                     :font-size 20
                                     :font-weight 400
                                     :line-height 1.4}
                :row-style {:height :auto
                            :align-items :flex-start}
                :header-top @filter-container-height-
                :headers (filterv identity
                                  [(when show-convo-details? "Session ID")
                                   "Timestamp"
                                   (when show-locations-column?
                                     "Location")
                                   "Duration"
                                   (cond-> {:column-id "marketing"
                                            :label (if ces-scores? "CES" "Marketing")
                                            :style (cond-> {:padding "0px 0px 0px 5px"}
                                                           ces-scores?
                                                           (assoc :justify-content :center
                                                                  :padding "0px 0px 0px 18px"))}

                                           (not ces-scores?)
                                           (assoc ;:expandable? true
                                                  :expanded-flex-val "0 0 215px"
                                                  :expanded-content [pipe-separated-header-list
                                                                     {:labels ["Upsize" "Combo" "Add-on"]
                                                                      :label-style {:padding "0 3px"}}]))
                                   {:column-id "efficiency"
                                    :label "Efficiency"
                                    :style {:padding "0px 0px 0px 5px"}
                                    :expandable? true
                                    :expanded-flex-val "0 0 174px"
                                    :expanded-content [pipe-separated-header-list {:labels ["Start" "Pace"]
                                                                                   :container-style {:margin-left 22}}]}
                                   {:column-id "satisfaction"
                                    :label @satisfaction-name-
                                    :style (merge {:padding "0px 0px 0px 5px"}
                                                  ;; Remove this if we want to go back to expandable
                                                  {:justify-content :center
                                                   :padding 0})
                                    ;:expandable? true
                                    :center-content? true
                                    :expanded-flex-val "0 0 215px"
                                    ;:expanded-content [pipe-separated-header-list {:labels ["Greeting" "Sentiment"]}]
                                    }
                                   {:column-id "outcome"
                                    :label "Outcome"
                                    :expandable? true
                                    :expanded-flex-val "0 0 135px"}
                                   {:label "Tags"
                                    :style {:padding "0px 0px 0px 5px"}}])
                :rows (map-indexed (fn [idx
                                        {:keys [conversation-id
                                                location-id
                                                dataset-id
                                                conversation-ts
                                                audio-source
                                                duration
                                                category-scores
                                                conversation-tags
                                                utterance-tags]}]
                                     (let [filtered-utterance-tags (->> utterance-tags
                                                                        (remove #(or (if show-menu-item-tags?
                                                                                       (tag.d/sessions-table-hidden-utterance-tag-names-excluding-menu-items (:name %))
                                                                                       (tag.d/sessions-table-hidden-utterance-tag-names (:name %)))
                                                                                     (role-hidden-tag-names (:name %))))
                                                                        (filterv :name)
                                                                        (group-by (fn [t] [(:name t) (:value t)]))
                                                                        (mapv (fn [[_ tags]]
                                                                                (assoc (first tags)
                                                                                  :count (count tags)
                                                                                  :children (mapcat :children tags)))))
                                           displayable-conversation-tags (->> conversation-tags
                                                                              (filter #(or (contains? #{"intent-summary"}
                                                                                                      (:name %))
                                                                                           (and (contains? #{"improvement-recommendation"}
                                                                                                           (:name %))
                                                                                                (not= (s/lower-case (:value %))
                                                                                                      "none"))))
                                                                              (filterv :name)
                                                                              (sort-by tag/tag->display-name))]
                                       {:cells
                                        (filterv identity
                                                 [(when show-convo-details?
                                                    {:label conversation-id
                                                     :copyable? true
                                                     :style {:color (style/alc-color "Text/secondary")
                                                             :white-space :nowrap}})
                                                  {:label [:span {:style {:color (style/alc-color "Text/secondary")}}
                                                           conversation-ts]}
                                                  (when show-locations-column?
                                                    {:label (get @location-id->label- (util/parse-int location-id))})
                                                  {:label (t/seconds->format duration)}
                                                  (if ces-scores?
                                                    {:label [score-tag {:score (some->> (tags.d/find-ces-score-tag conversation-tags)
                                                                                        :value
                                                                                        (#(when (not= (some-> %
                                                                                                              (str)
                                                                                                              (s/lower-case))
                                                                                                      "null")
                                                                                            %))
                                                                                        (util/parse-int))
                                                                        :score-thresholds [3 6]
                                                                        ;:score-thresholds [2 5]
                                                                        ;:low-score-best? true
                                                                        }]}
                                                    {:label [score-tag {:score (some->> (vi.d/matches-category "marketing" category-scores)
                                                                                        (first)
                                                                                        :score)}]
                                                     :expanded-content [:div {:style {:display :flex
                                                                                      :justify-content :space-around
                                                                                      :align-items :center
                                                                                      :width 132
                                                                                      :height 18
                                                                                      :border (when (not-empty conversation-tags)
                                                                                                (str "1px solid " (style/alc-color "Border/divider_secondary")))
                                                                                      :border-radius 999
                                                                                      :margin-left 18}}
                                                                        [tag-material-icon {:tags conversation-tags :name "size-upsell"}]
                                                                        [tag-material-icon {:tags conversation-tags :name "combo-upsell"}]
                                                                        [tag-material-icon {:tags conversation-tags :name "add-on-upsell"}]]
                                                     :style {:padding "0px 0px 0px 5px"}})
                                                  {:label [score-tag {:score (some->> (vi.d/matches-category "efficiency" category-scores)
                                                                                      (first)
                                                                                      :score)}]
                                                   :expanded-content (let [find-tag-val (fn [tag-name]
                                                                                          (some->> conversation-tags
                                                                                                   (filter #(and (= (:category %)
                                                                                                                    "efficiency")
                                                                                                                 (= (:name %)
                                                                                                                    tag-name)))
                                                                                                   (first)
                                                                                                   :value
                                                                                                   (hash-map :value)
                                                                                                   (tag/tag->display-name)))
                                                                           start-val (find-tag-val "start")
                                                                           pace-val (find-tag-val "pace")]
                                                                       [:div {:style {:display :flex
                                                                                      :margin-left 14}}
                                                                        [:span {:style {:font-size 12
                                                                                        :text-align :right
                                                                                        :flex "0 0 47px"}}
                                                                         start-val]
                                                                        [:span {:style {:font-size 12
                                                                                        :margin-left 12}}
                                                                         pace-val]])
                                                   :style {:padding "0px 0px 0px 5px"}}
                                                  {:label [score-tag {:score (some->> (vi.d/matches-category "satisfaction" category-scores)
                                                                                      (first)
                                                                                      :score)}]
                                                   ;:expanded-content [:div {:style {:display :flex
                                                   ;                                 :align-items :center
                                                   ;                                 :margin-left 29}}
                                                   ;                   [:div {:style {:padding "0 7px"
                                                   ;                                  :height 16}}
                                                   ;                    [tag-material-icon {:tags conversation-tags
                                                   ;                                        :name "greeting"}]]
                                                   ;                   [:div {:style {:padding "0 7px"
                                                   ;                                  :margin-left 15
                                                   ;                                  :height 16}}
                                                   ;                    [tag-material-icon {:tags conversation-tags
                                                   ;                                        :name "sentiment"}]]]
                                                   ;:style {:padding "0px 0px 0px 5px"}
                                                   :style {:justify-content :center
                                                           :padding 0}
                                                   }
                                                  (let [outcome (->> conversation-tags
                                                                     (filter #(contains? (if ai?
                                                                                           #{"ai-outcome"}
                                                                                           #{"outcome"})
                                                                                         (:name %)))
                                                                     (last))]
                                                    {:label [outcome-icon {:tag outcome}]
                                                     :expanded-content [outcome-label {:tag outcome}]})
                                                  {:label (->> (concat (mapv (fn [tag-info]
                                                                               [tag/utterance-tag-pill
                                                                                (assoc (select-keys tag-info [:name :value :children :count])
                                                                                  :show-description? true)])
                                                                             filtered-utterance-tags)
                                                                       (mapv (fn [tag-info]
                                                                               (let [intent? (= (:name tag-info) "intent-summary")
                                                                                     improvement? (= (:name tag-info) "improvement-recommendation")
                                                                                     outcome (when intent?
                                                                                               (->> tag-info
                                                                                                    :children
                                                                                                    (filter #(= (:name %)
                                                                                                                "outcome"))
                                                                                                    (first)
                                                                                                    :value))]
                                                                                 [tag/conversation-tag-pill
                                                                                  (cond-> (assoc (select-keys tag-info [:name :value :children :count])
                                                                                            :show-description? true)
                                                                                          improvement?
                                                                                          (assoc :hide-name? true
                                                                                                 :icon-type :lightbulb)

                                                                                          intent?
                                                                                          (assoc :hide-name? true
                                                                                                 :icon-type (condp = outcome
                                                                                                              "Resolved" :check
                                                                                                              "Partially Resolved" :remove
                                                                                                              "Not Resolved" :close
                                                                                                              nil)
                                                                                                 :description outcome))]))
                                                                             displayable-conversation-tags))
                                                               (filterv identity)
                                                               (into [:div {:style {:display :flex
                                                                                    :flex-wrap :wrap
                                                                                    :gap 8}}]))
                                                   :style {:flex-wrap :wrap
                                                           :padding "0 12px"}}])
                                        :on-click #(re/dispatch [:sessions/toggle-drawer-open {:conversation-id conversation-id
                                                                                               :dataset-id dataset-id
                                                                                               :conversation-ts conversation-ts
                                                                                               :convo-idx idx}])
                                        :actions-style {:height 24}
                                        :actions (when (and show-convo-details? ai?)
                                                   [{:icon-type :phone-in-talk
                                                     :on-click #(re/dispatch [:sessions/open-in-admin-click
                                                                              {:conversation-id conversation-id
                                                                               :audio-source audio-source
                                                                               :dataset-id dataset-id}])
                                                     :tooltip-content "Open in Admin"
                                                     :style {:padding 2}}])
                                        :hover-style (when (= selected-conversation-id conversation-id)
                                                       {:background-color (style/alc-color "Background/active_secondary")})
                                        :style (when (= selected-conversation-id conversation-id)
                                                 {:background-color (style/alc-color "Background/active_secondary")})}))
                                   @sessions-)}]))
